import { ICONS } from './icons'
import { getTranslations } from './i18n'
import type { WcagResult } from './wcag'
import { SUPPORTED_LANGS, type AccessibilityProfile, type AccessifyState, type ColorScheme, type Lang, type TextAlignment, type WidgetSize } from './types'

interface ProfileDef {
  id: AccessibilityProfile
  label: string
  icon: string
}

function stepperLabel(v: number, defaultLabel: string): string {
  if (v === 0) return defaultLabel
  return v > 0 ? `+${v}` : `${v}`
}

function sizeToggle(active: WidgetSize): string {
  return (['S', 'M', 'L'] as WidgetSize[])
    .map(s => `<button type="button" data-size="${s}" aria-pressed="${s === active}">${s}</button>`)
    .join('')
}

function themeToggle(active: ColorScheme, t: ReturnType<typeof getTranslations>): string {
  return `
    <div class="accessify-theme-toggle" role="group" aria-label="${t.theme}">
      <button type="button" data-scheme-option="light" aria-pressed="${active === 'light'}" aria-label="${t.lightMode}" title="${t.lightMode}">${ICONS.sun}</button>
      <button type="button" data-scheme-option="dark" aria-pressed="${active === 'dark'}" aria-label="${t.darkMode}" title="${t.darkMode}">${ICONS.moon}</button>
    </div>
  `
}

function profileCard(id: AccessibilityProfile, label: string, icon: string, active: boolean): string {
  return `
    <button class="accessify-card" type="button" data-profile="${id}" aria-pressed="${active}">
      <span class="icon">${icon}</span>
      <span class="label">${label}</span>
    </button>
  `
}

function stepperTile(opts: {
  key: string; icon: string; label: string; value: number; min: number; max: number; defaultLabel: string
}): string {
  const { key, icon, label, value, min, max, defaultLabel } = opts
  return `
    <div class="accessify-tile" data-stepper="${key}" aria-pressed="${value !== 0}" role="group" aria-label="${label}">
      <span class="icon">${icon}</span>
      <span class="label">${label}</span>
      <div class="accessify-stepper">
        <button type="button" data-step="-1" aria-label="Decrease ${label}" ${value <= min ? 'disabled' : ''}>${ICONS.chevronDown}</button>
        <span class="value">${stepperLabel(value, defaultLabel)}</span>
        <button type="button" data-step="1" aria-label="Increase ${label}" ${value >= max ? 'disabled' : ''}>${ICONS.chevronUp}</button>
      </div>
    </div>
  `
}

function toggleTile(opts: { key: string; icon: string; label: string; active: boolean }): string {
  return `
    <button class="accessify-tile" type="button" data-toggle="${opts.key}" aria-pressed="${opts.active}">
      <span class="icon">${opts.icon}</span>
      <span class="label">${opts.label}</span>
    </button>
  `
}

function alignmentTile(active: TextAlignment, label: string, alignLeft: string, alignCenter: string, alignRight: string): string {
  const btn = (a: TextAlignment, icon: string, ariaLabel: string) =>
    `<button type="button" data-align="${a}" aria-pressed="${active === a}" aria-label="${ariaLabel}">${icon}</button>`
  return `
    <div class="accessify-tile" aria-pressed="${active !== 'default'}" role="group" aria-label="${label}">
      <span class="icon">${ICONS.textAlignLeft}</span>
      <span class="label">${label}</span>
      <div class="accessify-align-group">
        ${btn('left', ICONS.textAlignLeft, alignLeft)}
        ${btn('center', ICONS.textAlignCenter, alignCenter)}
        ${btn('right', ICONS.textAlignRight, alignRight)}
      </div>
    </div>
  `
}

function colorTile(opts: { key: string; icon: string; label: string; active: boolean }): string {
  return `
    <button class="accessify-tile" type="button" data-color="${opts.key}" aria-pressed="${opts.active}">
      <span class="icon">${opts.icon}</span>
      <span class="label">${opts.label}</span>
    </button>
  `
}

export const SWATCH_COLORS = [
  '#0076b4',
  '#7a549c',
  '#c83733',
  '#d07021',
  '#26999f',
  '#4d7831',
  '#ffffff',
  '#000000',
] as const

function colorPickerCard(opts: {
  type: 'text' | 'title' | 'background'
  title: string
  currentColor: string | null
  cancelText: string
}): string {
  const swatchesHtml = SWATCH_COLORS.map(color => {
    const isSelected = opts.currentColor === color
    const isWhite = color === '#ffffff'
    return `<button type="button" class="accessify-swatch${isSelected ? ' active' : ''}${isWhite ? ' is-white' : ''}" data-color-target="${opts.type}" data-color-value="${color}" style="background-color: ${color};" aria-label="${color}" aria-pressed="${isSelected}"></button>`
  }).join('')

  return `
    <div class="accessify-color-card">
      <div class="accessify-color-card-title">${opts.title}</div>
      <div class="accessify-swatches">
        ${swatchesHtml}
      </div>
      <button type="button" class="accessify-color-cancel" data-cancel-color="${opts.type}">
        ${opts.cancelText}
      </button>
    </div>
  `
}

function scoreClass(result: WcagResult | null): string {
  if (!result) return ''
  if (result.score >= 80) return 'good'
  if (result.score >= 50) return 'ok'
  return 'poor'
}

function wcagIssuesHtml(result: WcagResult | null, t: ReturnType<typeof getTranslations>): string {
  if (!result) return ''
  if (result.issues.length === 0) {
    return `<div class="accessify-wcag-empty">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
      ${t.noIssues}
    </div>`
  }
  const rows = result.issues.map(issue => {
    const badge = issue.severity === 'fail' ? t.wcagFail : t.wcagWarn
    return `<div class="accessify-wcag-issue">
      <span class="accessify-wcag-badge ${issue.severity}">${badge}</span>
      <span class="accessify-wcag-issue-text">${issue.message}</span>
      <span class="accessify-wcag-issue-count">${issue.count}</span>
    </div>`
  }).join('')
  return `<div class="accessify-wcag-issues">${rows}</div>`
}

function wcagSection(result: WcagResult | null, scanning: boolean, t: ReturnType<typeof getTranslations>): string {
  const cls = scoreClass(result)
  const issueCount = result?.issues.filter(i => i.severity !== 'pass').length ?? 0
  const issuesHtml = wcagIssuesHtml(result, t)

  return `
    <div class="accessify-section">
      <div class="accessify-section-head">
        <span class="accessify-section-title">${t.pageAnalysis}</span>
      </div>
      ${result ? `
        <div class="accessify-wcag-score">
          <div class="accessify-wcag-score-circle ${cls}">${result.score}</div>
          <div class="accessify-wcag-score-meta">
            <div class="accessify-wcag-score-label">${t.score}: ${result.score}/100</div>
            <div class="accessify-wcag-score-sub">${issueCount} ${t.issuesFound}</div>
          </div>
        </div>
      ` : ''}
      <button type="button" class="accessify-wcag-analyze-btn" data-action="analyze" ${scanning ? 'disabled' : ''}>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" style="width:13px;height:13px" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.3-4.3"/><path d="M11 8v6M8 11h6"/></svg>
        ${scanning ? t.analyzing : t.analyzeNow}
      </button>
      ${issuesHtml}
    </div>
  `
}

const RESET_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>`

export function renderPanel(
  state: AccessifyState,
  size: WidgetSize,
  lang: Lang = 'en',
  wcagResult: WcagResult | null = null,
  wcagScanning = false,
  brandTitle?: string,
  statementOpen = false,
  scheme: ColorScheme = 'light',
): string {
  const t = getTranslations(lang)
  const title = brandTitle ?? t.title

  const PROFILES: Array<{ id: AccessibilityProfile; label: string; icon: string }> = [
    { id: 'seizure-safe',         label: t.seizureSafe,         icon: ICONS.seizure },
    { id: 'vision-impaired',      label: t.visionImpaired,      icon: ICONS.vision },
    { id: 'adhd-friendly',        label: t.adhdFriendly,        icon: ICONS.adhd },
    { id: 'cognitive-disability', label: t.cognitiveDisability,  icon: ICONS.cognitive },
    { id: 'keyboard-navigation',  label: t.keyboardNavigation,  icon: ICONS.keyboard },
    { id: 'screen-reader',        label: t.screenReader,        icon: ICONS.screenReader },
    { id: 'color-blind',          label: t.colorBlind,          icon: ICONS.colorBlind },
    { id: 'dyslexia',             label: t.dyslexia,            icon: ICONS.dyslexia },
  ]

  const dir = lang === 'ar' ? ' dir="rtl"' : ''
  const currentLang = SUPPORTED_LANGS.find(l => l.code === lang) ?? { code: lang, label: String(lang).toUpperCase(), flag: '🌐' }

  return `
    <div class="accessify-header"${dir}>
      <div class="accessify-header-left">
        <div class="accessify-header-icon">${ICONS.wheelchair}</div>
        <div class="accessify-header-text">
          <div class="accessify-header-title">${title}</div>
          <div class="accessify-header-sub">${t.subtitle}</div>
        </div>
      </div>
      <div class="accessify-header-actions">
        <div class="accessify-lang-picker">
          <button type="button" class="accessify-lang-btn" aria-label="${t.language}" aria-haspopup="listbox" aria-expanded="false" data-action="toggle-lang-menu">
            <span class="accessify-lang-flag">${currentLang.flag}</span>
            <span class="accessify-lang-name">${currentLang.label}</span>
            <svg class="accessify-lang-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <div class="accessify-lang-menu" role="listbox" aria-label="${t.language}">
            ${SUPPORTED_LANGS.map(l => `
              <button type="button" class="accessify-lang-item${l.code === lang ? ' active' : ''}" role="option" data-lang="${l.code}" value="${l.code}" aria-selected="${l.code === lang}">
                <span class="accessify-lang-flag">${l.flag}</span>
                <span class="accessify-lang-item-label">${l.label}</span>
                ${l.code === lang ? `<svg class="accessify-lang-check" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>` : ''}
              </button>
            `).join('')}
          </div>
        </div>
        <button type="button" class="accessify-icon-btn accessify-close" aria-label="${t.close}">${ICONS.close}</button>
      </div>
    </div>

    <div class="accessify-body"${dir}>

      <div class="accessify-section accessify-section--compact">
        <div class="accessify-section-head">
          <span class="accessify-section-title">${t.widgetSize}</span>
          <div class="accessify-head-controls">
            <div class="accessify-size-toggle" role="group" aria-label="${t.widgetSize}">${sizeToggle(size)}</div>
            ${themeToggle(scheme, t)}
          </div>
        </div>
      </div>

      <div class="accessify-section">
        <div class="accessify-section-head">
          <span class="accessify-section-title">${t.profiles}</span>
        </div>
        <div class="accessify-grid">
          ${PROFILES.map(p => profileCard(p.id, p.label, p.icon, state.profile === p.id)).join('')}
        </div>
      </div>

      <div class="accessify-section">
        <div class="accessify-section-head">
          <span class="accessify-section-title">${t.contentAdjustments}</span>
        </div>
        <div class="accessify-grid accessify-grid-3">
          ${stepperTile({ key: 'contentScale',   icon: ICONS.contentScaling,  label: t.contentScale,   value: state.contentScale,  min: -5, max: 5,  defaultLabel: t.default })}
          ${toggleTile({  key: 'readableFont',   icon: ICONS.readableFont,    label: t.readableFont,   active: state.readableFont })}
          ${toggleTile({  key: 'highlightTitles',icon: ICONS.highlightTitles, label: t.highlightTitles,active: state.highlightTitles })}
          ${stepperTile({ key: 'fontSize',       icon: ICONS.fontSizing,      label: t.fontSize,       value: state.fontSize,      min: -5, max: 10, defaultLabel: t.default })}
          ${toggleTile({  key: 'textMagnifier',  icon: ICONS.textMagnifier,   label: t.textMagnifier,  active: state.textMagnifier })}
          ${toggleTile({  key: 'highlightLinks', icon: ICONS.highlightLinks,  label: t.highlightLinks, active: state.highlightLinks })}
          ${toggleTile({  key: 'readingLens',    icon: ICONS.readingLens,     label: t.readingLens,    active: state.readingLens })}
          ${stepperTile({ key: 'lineHeight',     icon: ICONS.lineHeight,      label: t.lineHeight,     value: state.lineHeight,    min: -2, max: 6,  defaultLabel: t.default })}
          ${stepperTile({ key: 'letterSpacing',  icon: ICONS.letterSpacing,   label: t.letterSpacing,  value: state.letterSpacing, min: -2, max: 6,  defaultLabel: t.default })}
          ${alignmentTile(state.textAlignment, t.textAlign, t.alignLeft, t.alignCenter, t.alignRight)}
        </div>
      </div>

      <div class="accessify-section">
        <div class="accessify-section-head">
          <span class="accessify-section-title">${t.colorAdjustments}</span>
        </div>
        <div class="accessify-grid accessify-grid-3 accessify-color-adjustments-grid">
          ${colorTile({ key: 'darkContrast',   icon: ICONS.darkContrast,   label: t.darkContrast,   active: state.darkContrast })}
          ${colorTile({ key: 'lightContrast',  icon: ICONS.lightContrast,  label: t.lightContrast,  active: state.lightContrast })}
          ${colorTile({ key: 'highContrast',   icon: ICONS.highContrast,   label: t.highContrast,   active: state.highContrast })}

          ${colorTile({ key: 'highSaturation', icon: ICONS.highSaturation, label: t.highSaturation, active: state.highSaturation })}
          ${colorPickerCard({ type: 'text', title: t.adjustTextColors, currentColor: state.textColor, cancelText: t.cancel })}

          ${colorTile({ key: 'monochrome',     icon: ICONS.monochrome,     label: t.monochrome,     active: state.monochrome })}
          ${colorPickerCard({ type: 'title', title: t.adjustTitleColors, currentColor: state.titleColor, cancelText: t.cancel })}

          ${colorTile({ key: 'lowSaturation',  icon: ICONS.lowSaturation,  label: t.lowSaturation,  active: state.lowSaturation })}
          ${colorPickerCard({ type: 'background', title: t.adjustBackgroundColors, currentColor: state.backgroundColor, cancelText: t.cancel })}
        </div>
      </div>

      <div class="accessify-section">
        <div class="accessify-section-head">
          <span class="accessify-section-title">${t.orientationAdjustments}</span>
        </div>
        <div class="accessify-grid">
          ${toggleTile({ key: 'readingGuide',   icon: ICONS.readingGuide,   label: t.readingGuide,   active: state.readingGuide })}
          ${toggleTile({ key: 'stopAnimations', icon: ICONS.stopAnimations, label: t.stopAnimations, active: state.stopAnimations })}
          ${toggleTile({ key: 'readingMask',    icon: ICONS.readingMask,    label: t.readingMask,    active: state.readingMask })}
          ${toggleTile({ key: 'highlightHover', icon: ICONS.highlightHover, label: t.highlightHover, active: state.highlightHover })}
          ${toggleTile({ key: 'bigBlackCursor', icon: ICONS.bigBlackCursor, label: t.bigBlackCursor, active: state.bigBlackCursor })}
          ${toggleTile({ key: 'bigWhiteCursor', icon: ICONS.bigWhiteCursor, label: t.bigWhiteCursor, active: state.bigWhiteCursor })}
        </div>
      </div>

      <div class="accessify-section">
        <div class="accessify-section-head">
          <span class="accessify-section-title">${t.statement}</span>
        </div>
        <button type="button" class="accessify-statement-card" data-action="open-statement" aria-haspopup="dialog" aria-expanded="${statementOpen}">
          <div class="accessify-statement-card-left">
            <div class="accessify-statement-card-icon">
              ${ICONS.statement}
            </div>
            <div class="accessify-statement-card-info">
              <span class="accessify-statement-card-title">${t.statementTitle}</span>
              <span class="accessify-statement-card-desc">${t.statementDesc}</span>
            </div>
          </div>
          <div class="accessify-statement-card-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="9 18 15 12 9 6"/></svg>
          </div>
        </button>
      </div>

      ${/* Page Analysis section
      wcagSection(wcagResult, wcagScanning, t)
      */ ''}

    </div>

    <div class="accessify-reset-bar"${dir}>
      <button type="button" class="accessify-reset-btn" data-action="reset" aria-label="${t.resetAll}">
        ${RESET_ICON}
        ${t.resetAll}
      </button>
    </div>

    ${statementOpen ? `
      <div class="accessify-statement-modal" role="dialog" aria-modal="true" aria-labelledby="acc-statement-title"${dir}>
        <div class="accessify-statement-header">
          <button type="button" class="accessify-statement-close" data-action="close-statement" aria-label="${t.closeStatement}" title="${t.closeStatement}">
            ${ICONS.closeBox}
          </button>
        </div>
        <div class="accessify-statement-body">
          <h2 id="acc-statement-title" class="accessify-statement-title">${t.statementTitle}</h2>
          <h3 class="accessify-statement-heading">${t.complianceStatus}</h3>
          <p class="accessify-statement-text">${t.statementP1}</p>
          <p class="accessify-statement-text">${t.statementP2}</p>
          <p class="accessify-statement-text">${t.statementP3}</p>
        </div>
      </div>
    ` : ''}
  `
}
